import { api } from '@/lib/api/client';
import type {
  CalendarEvent,
  EventsResponse,
  EventFilters,
  CreateEventDto,
  UpdateEventDto,
  DisplayEvent,
  DisplayUser,
} from '../types';

interface CalendarPageFilters {
  'date.min'?: Date;
  'date.max'?: Date;
  createdById?: number;
  attendeeId?: number;
  clientId?: number;
  matterId?: number;
  target?: string;
}

export async function fetchEvents(filters: EventFilters | CalendarPageFilters): Promise<EventsResponse> {
  const params: Record<string, unknown> = {
    limit: 100,
    offset: 0,
  };

  // Handle EventFilters format
  if ('page' in filters && 'limit' in filters) {
    const eventFilters = filters as EventFilters;
    params.limit = eventFilters.limit;
    params.offset = eventFilters.page * eventFilters.limit;

    if (eventFilters.s && eventFilters.s.length >= 2) {
      params['s'] = eventFilters.s;
    }

    if (eventFilters.orderBy && eventFilters.order) {
      params['orderBy'] = eventFilters.orderBy;
      params['order'] = eventFilters.order;
    }

    if (eventFilters['startsAt.min']) {
      const minDate = new Date(eventFilters['startsAt.min']);
      minDate.setHours(0, 0, 0, 0);
      params['startsAt.min'] = minDate.toISOString();
    }

    if (eventFilters['startsAt.max']) {
      const maxDate = new Date(eventFilters['startsAt.max']);
      maxDate.setHours(23, 59, 59, 999);
      params['startsAt.max'] = maxDate.toISOString();
    }

    if (eventFilters.typeId) {
      params['typeId'] = eventFilters.typeId;
    }

    if (eventFilters.matterId) {
      params['matterId'] = eventFilters.matterId;
    }

    if (eventFilters.clientId) {
      params['clientId'] = eventFilters.clientId;
    }

    if (eventFilters.userId) {
      params['userId'] = eventFilters.userId;
    }
  }

  // Handle CalendarPageFilters format (from react-big-calendar page)
  // Spread all filters like the old project does
  const calendarFilters = filters as CalendarPageFilters;

  if (calendarFilters['date.min']) {
    params['date.min'] = calendarFilters['date.min'].toISOString();
  }

  if (calendarFilters['date.max']) {
    params['date.max'] = calendarFilters['date.max'].toISOString();
  }

  if (calendarFilters.createdById) {
    params['createdById'] = calendarFilters.createdById;
  }

  if (calendarFilters.attendeeId) {
    params['attendeeId'] = calendarFilters.attendeeId;
  }

  if (calendarFilters.clientId) {
    params['clientId'] = calendarFilters.clientId;
  }

  if (calendarFilters.matterId) {
    params['matterId'] = calendarFilters.matterId;
  }

  if (calendarFilters.target) {
    params['target'] = calendarFilters.target;
  }

  return api.get<EventsResponse>('/events', { params });
}

export async function fetchEvent(id: number): Promise<CalendarEvent> {
  return api.get<CalendarEvent>(`/events/${id}`);
}

export async function createEvent(data: CreateEventDto): Promise<CalendarEvent> {
  return api.post<CalendarEvent>('/events', data);
}

export async function updateEvent(id: number, data: UpdateEventDto): Promise<CalendarEvent> {
  return api.patch<CalendarEvent>(`/events/${id}`, data);
}

export async function deleteEvent(id: number): Promise<void> {
  return api.delete(`/events/${id}`);
}

// Helper to transform API events to display events
export function transformToDisplayEvents(events: CalendarEvent[]): DisplayEvent[] {
  return events.map((event) => ({
    id: event.id,
    title: event.title,
    description: event.description,
    startDate: event.startsAt,
    endDate: event.endsAt,
    color: event.type?.color,
    user: {
      id: event.createdBy?.id ?? 0,
      name: event.createdBy?.name ?? 'Unknown',
      picturePath: event.createdBy?.picturePath ?? null,
    },
  }));
}

// Fetch users for calendar filters
export async function fetchUsers(filters: { page: number; limit: number }): Promise<{
  results: DisplayUser[];
  meta: { total: number };
}> {
  const offset = filters.page * filters.limit;
  const response = await api.get<{
    results: Array<{ id: number; name: string; picturePath?: string | null }>;
    meta: { total: number };
  }>('/users', {
    params: { limit: filters.limit, offset },
  });

  return {
    results: response.results.map((user) => ({
      id: user.id,
      name: user.name,
      picturePath: user.picturePath ?? null,
    })),
    meta: response.meta,
  };
}

// Fetch event types
export async function fetchEventTypes(): Promise<Array<{ id: number; name: string; color?: string }>> {
  const response = await api.get<{
    results: Array<{ id: number; name: string; color?: string }>;
  }>('/event-types', { params: { limit: 100 } });
  return response.results;
}
